import React from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuid } from 'uuid';
import { usePortal } from '../../hooks/usePortal';
import { Notification, NotificationConfig } from './Notification';

type NotificationActionType = (
  _config: Omit<NotificationConfig, 'status'>
) => void;



type NotificationContextType = {
  notifications: any[];
  /**
   * Adds a new warning notification.
   */
  warn: NotificationActionType;
  /**
   * Adds a new error notification.
   */
  error: NotificationActionType;
  /**
   * Adds a new success notification.
   */
  success: NotificationActionType;
  /**
   * Adds a new info notification.
   */
  info: NotificationActionType;
  /**
   * Adds a new notification.
   */
  open: (_config: NotificationConfig) => void;
  /**
   * Closes a notification with given id.
   */
  close: (_id: string) => void;
  /**
   * Closes all notifications.
   */
  destroy: () => void;
};

const noop = () => {};

const NotificationContext = React.createContext<NotificationContextType>({
  notifications: [],
  warn: noop,
  error: noop,
  success: noop,
  info: noop,
  open: noop,
  close: noop,
  destroy: noop,
});

export const useNotification = () => React.useContext(NotificationContext);

export const NotificationProvider: React.FC<React.PropsWithChildren<unknown>> =
  ({ children }) => {
    const [notifications, setNotifications] = React.useState<
      NotificationConfig[]
    >([]);
    const notificationPortal = usePortal('notification-portal');

    const removeNotification = (id: string) => {
      setNotifications((_notifications) =>
        _notifications.filter((notification) => notification.id !== id)
      );
    };

    const addNotification = ({
      id = uuid(),
      ...notification
    }: NotificationConfig) => {
      // Functional updater — two notifications fired in the same tick both
      // land (the old spread of the closure's `notifications` lost one).
      setNotifications((prev) => [
        ...prev,
        {
          ...notification,
          id,
          onClose: () => {
            notification.onClose?.();
            removeNotification(id);
          },
        },
      ]);
    };

    const warn = (config: NotificationConfig) => {
      addNotification({
        ...config,
        status: 'warning',
      });
    };

    const error = (config: NotificationConfig) => {
      addNotification({
        ...config,
        status: 'error',
      });
    };

    const success = (config: NotificationConfig) => {
      addNotification({
        ...config,
        status: 'success',
      });
    };

    const info = (config: NotificationConfig) => {
      addNotification({
        ...config,
        status: 'info',
      });
    };

    const open = (config: NotificationConfig) => {
      addNotification(config);
    };

    const close = (id: string) => {
      removeNotification(id);
    };

    const destroy = () => {
      setNotifications([]);
    };

    return (
      <NotificationContext.Provider
        value={{
          notifications,
          close,
          destroy,
          error,
          info,
          success,
          warn,
          open,
        }}
      >
        <>
          {children}
          {ReactDOM.createPortal(
            <>
              {['topRight', 'topLeft', 'bottomRight', 'bottomLeft'].map(
                (position, index) => (
                  <div
                    key={index}
                    className={`notification-container notification-container-${position}`}
                  >
                    {notifications
                      .filter(
                        (notification) =>
                          (notification.placement || 'topRight') === position
                      )
                      .map((notification) => (
                        <Notification
                          key={notification.id}
                          config={notification}
                        />
                      ))}
                  </div>
                )
              )}
            </>,
            notificationPortal
          )}
        </>
      </NotificationContext.Provider>
    );
  };
